import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/* SINHALA BOOKS USA — the two phone header controllers (added 2026-09-16,
   rewritten the same day).
   Operator, 2026-09-16, after asking what is industry standard: "the most
   common pattern… this default pattern" — Shopify's own phone row, [☰] [🔍]
   wordmark [👤] [🛒]. decisions/2026-09-16-mobile-header-shopify-default.md.
   Both run under 900px only: from 900px the shelf sidebar is a docked rail and
   the search field is inline (the drawer at every width was built and reversed
   on 2026-10-04, operator: "collapsing it in browser mode might be a mistake"
   — decisions/2026-10-04-reversal-the-left-nav-stays-docked-on-desktop-and-
   the-banner-gets-a-220px-cap.md).
   Loaded by snippets/sbu-nav-toggle.liquid, which renders both buttons in every
   header; the header's buttons reach these elements through
   on:click="#id/method" — assets/component.js resolves '#id' targets and calls
   the method synchronously from its document click listener.

   <sbu-nav-drawer> (SbuNavDrawer) — an empty element beside the shelf sidebar's
   <aside> (sections/sbu-shelf-sidebar.liquid). It never moves markup: it flips
   [data-open] on the aside and the section's CSS slides the list in from the
   left. It opens only while `small` matches and closes itself when the window
   crosses 900px, where the aside is the docked rail. The focus trap is NOT
   Horizon's trapFocus (assets/focus.js), which traps one container: the list
   sits in the page and its close button (☰ showing an X) sits in the header,
   so #trapFocus cycles the toggle plus the list's links. While open, every
   section wrapper around the aside and the footer are `inert`, so the dimmed
   page behind the backdrop takes neither a click nor focus; the header is
   left live (its controls close the drawer, below).

   <sbu-search-reveal> (SbuSearchReveal) — an empty element next to 🔍. It flips
   [data-sbu-search-open] on #header-component and snippets/sbu-nav-toggle.liquid
   lays the existing SBU field (snippets/sbu-search-field.liquid,
   #sbu-header-search) out as a second header row.

   The two are mutually exclusive: each open() closes the other first, without
   restoring focus. Scroll lock is Horizon's owner-counted lockScroll
   (assets/utilities.js), so a close-then-open hand-off never leaves
   html[scroll-lock] stuck. Both re-attach on connect with an AbortController, as
   assets/sbu-shelf-rail.js does, so the theme editor and the Section Rendering
   API cannot leave a stale listener or a stuck html[scroll-lock]. */

/* Both controllers read this: from 900px the rail is docked and the inline
   field renders, so neither the drawer nor the reveal may open there. */
const small = matchMedia('(max-width: 899px)');
const modalOpen = () => document.querySelector('dialog:modal') !== null;

/* A mouse window narrower than 900px has a page scrollbar; lockScroll's
   overflow:hidden on <html> removes it and the whole header slid right by its
   width (379 -> 390px at 390, 2026-09-16, verifier). Reserve the gutter only when
   one was there, so a touch screen or a page that does not scroll gains no strip.
   Call AFTER closing the other controller: its close() clears the gutter. */
function reserveGutter() {
  const root = document.documentElement;
  if (window.innerWidth - root.clientWidth > 0) root.style.scrollbarGutter = 'stable';
}

class SbuNavDrawer extends Component {
  #ac;
  #trap;
  /** @type {Set<Element>} the elements THIS controller made inert, so close() never clears an inert set by something else */
  #inerted = new Set();

  get panel() { return document.getElementById(this.dataset.panel || 'sbu-nav-drawer-panel'); }
  get button() { return document.getElementById('sbu-nav-toggle'); }
  get isOpen() { return this.panel?.hasAttribute('data-open') ?? false; }

  connectedCallback() {
    super.connectedCallback();
    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const panel = this.panel;
    if (!panel) return;

    small.addEventListener('change', () => { if (!small.matches) this.close({ restoreFocus: false }); }, { signal });
    window.addEventListener('pagehide', () => this.close({ restoreFocus: false }), { signal });
    document.addEventListener('keydown', (e) => {
      // A modal dialog (the cart drawer) owns Escape while it is open.
      if (e.key !== 'Escape' || !this.isOpen || e.defaultPrevented || e.isComposing || modalOpen()) return;
      e.preventDefault();
      this.close();
    }, { signal });
    panel.addEventListener('click', (e) => {
      if (e.target === panel) return this.close();                       // the ::before backdrop
      if (e.target instanceof Element && e.target.closest('a[href]')) this.close({ restoreFocus: false }); // navigating
    }, { signal });
    /* HEADER CONTROLS WIN (2026-09-16, recheck R1). A press on any header control
       other than ☰ closes the menu BEFORE that control's own click runs, without
       preventDefault: Cart then opens its <dialog> with no trap underneath (it
       used to leave focus stuck on the drawer's Close with the menu and the lock
       still on), Account opens its popover, the wordmark navigates, 🔍 opens
       search. */
    document.addEventListener('pointerdown', (e) => {
      if (!this.isOpen || !(e.target instanceof Element)) return;
      if (!e.target.closest('#header-component') || e.target.closest('#sbu-nav-toggle')) return;
      this.close({ restoreFocus: false });
    }, { signal, capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.close({ restoreFocus: false });
    this.#ac?.abort();
  }

  toggle() { this.isOpen ? this.close() : this.open(); }

  open() {
    const panel = this.panel;
    if (!panel || !small.matches || this.isOpen) return;
    /** @type {any} */ (document.getElementById('sbu-search-reveal'))?.close?.({ restoreFocus: false });
    panel.setAttribute('data-open', '');
    this.button?.setAttribute('aria-expanded', 'true');
    reserveGutter();
    lockScroll(panel);
    this.#setInert(panel);
    const inner = panel.querySelector('.sbu-shelf-sidebar__inner') ?? panel;
    this.#trapFocus(inner);
    (inner.querySelector('[aria-current="page"]') ?? inner.querySelector('a[href]'))?.focus({ preventScroll: true });
  }

  /* Tab order while open: the toggle (now the X), then every link and control in the list,
     then round again; Shift+Tab the reverse. Focus that lands anywhere else
     (a click on the page behind the backdrop cannot, but a script can) is
     pulled back to the first link, as Horizon's trapFocus does — except
     (2026-09-16, recheck R1/R2):
     - while a modal dialog is open, the trap stands down entirely: the dialog
       owns Tab and focus (theme editor, or a dialog a script opens);
     - focus that lands on a header control is left alone: the pointerdown rule
       in connectedCallback is closing the menu for that control anyway. */
  #trapFocus(inner) {
    this.#trap?.abort();
    this.#trap = new AbortController();
    const { signal } = this.#trap;
    // Form controls too (added 2026-10-05): the All Books filter panel sits in the drawer.
    // A closed <details> keeps layout boxes for its body (content-visibility), so
    // getClientRects alone lets in controls that cannot take focus, and Tab stalled
    // on the "Show all" summary; only that summary counts while it is closed.
    const folded = (el) => {
      const d = el.closest('details:not([open])');
      return !!d && !(el.localName === 'summary' && el.parentElement === d);
    };
    const stops = () => [this.button, ...inner.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), summary')]
      .filter((el) => el instanceof HTMLElement && el.getClientRects().length > 0 && !folded(el));
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen || modalOpen()) return;
      if (e.key !== 'Tab' || e.defaultPrevented) return;
      const list = stops();
      if (!list.length) return;
      e.preventDefault();
      const at = list.indexOf(document.activeElement);
      const next = at < 0 ? (e.shiftKey ? list.length - 1 : 0) : (at + (e.shiftKey ? -1 : 1) + list.length) % list.length;
      list[next].focus();
    }, { signal, capture: true });
    document.addEventListener('focusin', (e) => {
      if (!this.isOpen || modalOpen()) return;
      const t = e.target;
      if (t instanceof Node && (inner.contains(t) || t === this.button)) return;
      if (t instanceof Element && t.closest('#header-component')) return;
      inner.querySelector('a[href]')?.focus();
    }, { signal, capture: true });
  }

  /* The page behind the backdrop is inert while the drawer is open (2026-10-04;
     in the 750-899 band the backdrop sits beside a 360px drawer and a mouse
     could otherwise reach a link under the dim; kept when the drawer went back
     under 900px). The aside is itself a section in main, so main cannot be inert as
     a whole: every OTHER section wrapper in main and the footer are, the header
     is not (its controls close the drawer first, the pointerdown rule above).
     Only elements that were not already inert are touched, and only those are
     released, so a dialog's own inert is never undone from here. */
  #setInert(panel) {
    const own = panel.closest('.shopify-section');
    const main = own?.parentElement;
    const targets = [...(main ? main.children : []), ...document.querySelectorAll('.footer-group, footer')]
      .filter((el) => el !== own && el instanceof HTMLElement && !el.inert);
    for (const el of targets) { el.inert = true; this.#inerted.add(el); }
  }

  #clearInert() {
    for (const el of this.#inerted) el.inert = false;
    this.#inerted.clear();
  }

  close({ restoreFocus = true } = {}) {
    const panel = this.panel;
    if (!panel || !this.isOpen) return;
    panel.removeAttribute('data-open');
    this.button?.setAttribute('aria-expanded', 'false');
    unlockScroll(panel);
    this.#clearInert();
    document.documentElement.style.scrollbarGutter = '';
    this.#trap?.abort();
    this.#trap = undefined;
    if (restoreFocus) this.button?.focus();
  }
}

/* 🔍 reveals the existing field (2026-09-16). Not Horizon's search modal
   (snippets/search-modal.liquid + assets/dialog.js): that is a <dialog> with its
   own results markup and ids (the H7 duplicate-id problem), none of the SBU
   colour resets and no Sinhala placeholder. */
class SbuSearchReveal extends Component {
  #ac;

  get header() { return document.getElementById('header-component'); }
  get wrap() { return document.getElementById('sbu-header-search'); }
  get input() { return this.wrap?.querySelector('#sbu-search-input'); }
  get button() { return document.getElementById('sbu-search-toggle'); }
  get isOpen() { return this.header?.hasAttribute('data-sbu-search-open') ?? false; }

  connectedCallback() {
    super.connectedCallback();
    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    small.addEventListener('change', () => { if (!small.matches) this.close({ restoreFocus: false }); }, { signal });
    window.addEventListener('pagehide', () => this.close({ restoreFocus: false }), { signal });
    // Escape closes and returns focus to 🔍. The stock onSearchKeyDown sees the
    // same Escape and clears the input; both happen, that is the intent.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.isOpen || e.defaultPrevented || e.isComposing || modalOpen()) return;
      this.close();
    }, { signal });
    // A tap outside the field closes it (the backdrop on body, the wordmark, any
    // header control). No preventDefault: the tapped control still acts — Cart
    // opens, the wordmark navigates. closest(), not ===: taps land on the icon's
    // span or svg. A result link inside the field closes it too (navigating).
    document.addEventListener('click', (e) => {
      if (!this.isOpen) return;
      const t = e.target instanceof Element ? e.target : null;
      if (t?.closest('#sbu-search-toggle')) return;
      const wrap = this.wrap;
      if (t && wrap?.contains(t)) {
        if (t.closest('a[href]')) this.close({ restoreFocus: false });
        return;
      }
      this.close({ restoreFocus: false });
    }, { signal, capture: true });
    /* FOCUS LEAVING THE HEADER CLOSES IT (2026-09-16, verifier; a gap in the
       plan's §2.3). Tab from the field ran submit -> Account -> Cart and then
       into the page under the backdrop — dimmed, untappable, scroll-locked —
       with search still open. Horizon's own search is a modal dialog that keeps
       focus; this is the lighter equivalent: the reveal stays open while focus
       is anywhere in the header (the field, ✕, the wordmark, Account, Cart —
       Shift+Tab from the input passes the wordmark first) and closes, without
       moving focus, the moment it lands outside. Not a trap like the menu's:
       the field's own Tab handler (snippets/sbu-search-field.liquid, TRAP
       2026-09-14) stops Tab on window capture, and it deliberately lets focus
       leave the field (WCAG 2.1.2). focusin, not focusout: it names the
       destination, and focus going to the browser chrome does not close. */
    document.addEventListener('focusin', (e) => {
      if (!this.isOpen || modalOpen()) return;
      if (e.target instanceof Element && e.target.closest('#header-component')) return;
      this.close({ restoreFocus: false });
    }, { signal, capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.close({ restoreFocus: false });
    this.#ac?.abort();
  }

  toggle() { this.isOpen ? this.close() : this.open(); }

  open() {
    const wrap = this.wrap;
    if (!small.matches || this.isOpen || !wrap || !this.header) return;
    /** @type {any} */ (document.getElementById('sbu-nav-drawer'))?.close?.({ restoreFocus: false });
    this.header.setAttribute('data-sbu-search-open', '');
    this.button?.setAttribute('aria-expanded', 'true');
    reserveGutter();
    lockScroll(wrap);
    // SYNCHRONOUSLY, in the tap's own task: iOS and Android raise the software
    // keyboard only when focus() runs inside the user gesture. Never move this
    // into requestAnimationFrame, a timeout or after an await (DialogComponent's
    // showDialog does; do not copy that shape here).
    this.input?.focus();
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    this.header?.removeAttribute('data-sbu-search-open');
    this.button?.setAttribute('aria-expanded', 'false');
    if (this.wrap) unlockScroll(this.wrap);
    document.documentElement.style.scrollbarGutter = '';
    if (restoreFocus) this.button?.focus();
  }
}

if (!customElements.get('sbu-nav-drawer')) customElements.define('sbu-nav-drawer', SbuNavDrawer);
if (!customElements.get('sbu-search-reveal')) customElements.define('sbu-search-reveal', SbuSearchReveal);

/**
 * DESKTOP LAYOUT ON A TOUCH SCREEN (2026-09-16, recheck O3). Horizon's
 * setHeaderMenuStyle() (assets/utilities.js) writes data-menu-style="drawer"
 * on every touch device at every width, and every drawer rule in the theme
 * keys off that attribute (sections/header.liquid, snippets/search.liquid,
 * blocks/_header-menu.liquid): an iPad at 1024 got Horizon's two-line drawer
 * and magnifier beside the SBU desktop rail, with the inline search hidden —
 * a third header. From 900px the SBU layout is the desktop one whatever the
 * pointer, so the attribute is rewritten to "menu" there. A MutationObserver,
 * not a patch of Horizon's function: the observer's microtask runs after
 * Horizon's rAF write and before paint, so nothing flashes. The one case
 * Horizon uses "drawer" on a mouse — the overflow-list reporting
 * minimum-reached — is left alone. Under 900 nothing changes: the SBU row
 * already covers both attribute values there (snippets/sbu-nav-toggle.liquid).
 * The observer watches the document subtree for that one attribute and
 * re-finds #header-component on every call, rather than binding to the element
 * that existed at load: the theme editor and the Section Rendering API replace
 * the header, and Horizon writes "drawer" on the new one.
 */
const desktop = window.matchMedia('(min-width: 900px)');
const settleMenuStyle = () => {
  const headerComponent = document.getElementById('header-component');
  if (!headerComponent || !desktop.matches || headerComponent.dataset.menuStyle !== 'drawer') return;
  const overflowList = headerComponent.querySelector('overflow-list');
  if (overflowList?.hasAttribute('minimum-reached')) return;
  headerComponent.dataset.menuStyle = 'menu';
};
new MutationObserver(settleMenuStyle).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['data-menu-style'] });
desktop.addEventListener('change', settleMenuStyle);
settleMenuStyle();
